import { FilterGroup, FilterSection } from '@/components/ConfigurationEditors/FilterGroup';
import { ReportRunConfigWizardContext } from '@/components/ConfigurationEditors/ReportConfigurationEditor/ReportRunConfigWizard/ReportRunConfigWizardContext';
import { SelectorLabelInfo } from '@/components/SelectorLabelInfo';
import { FocusKPI } from '@/types/ApiTypes';
import { ReportRunConfig } from '@/types/ReportRun';
import { ByzzerSelect } from '@byzzer/ui-components';
import classnames from 'classnames';
import React, { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';

export type ReportKPIConfigFiltersProps = {
    name?: string;
    onChange?: (e: ByzzerChangeEvent<FocusKPI>) => void;
    onValidityChange?: (e: ByzzerValidityChangeEvent) => void;
    value?: FocusKPI;
    includeOmniShopperKPI?: boolean;
    requireOmniShopperKPI?: boolean;
} & Partial<Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>>;

const preDefinedKPISelectors: FocusKPI[] = ['Value (Dollar volume)', 'Units'];

const baseClassName = 'report-kpi-config-filters';

type ReportKPIConfigFiltersValue = Partial<ReportRunConfig>;

export type ReportKPIConfigFiltersRef = {
    value: ReportKPIConfigFiltersValue;
    isValid: boolean;
};

const ReportKPIConfigFilters = forwardRef<ReportKPIConfigFiltersRef, ReportKPIConfigFiltersProps>(
    (
        {
            className,
            onChange,
            onValidityChange,
            name,
            value,
            includeOmniShopperKPI = false,
            requireOmniShopperKPI = false,
            ...props
        },
        ref
    ) => {
        const { value: contextValue, onChange: onContextChange, sku } = useContext(ReportRunConfigWizardContext);
        const [internalValue, setInternalValue] = useState<ReportKPIConfigFiltersValue>({});
        const wasValid = useRef<boolean>(false);

        useEffect(() => {
            const isValid = checkValidity();

            if (wasValid.current !== isValid) {
                onValidityChange?.({
                    name,
                    isValid,
                });

                wasValid.current = isValid;
            }
        }, [internalValue]);

        useEffect(() => {
            setInternalValue(contextValue ?? {});
        }, [contextValue]);

        useImperativeHandle(
            ref,
            () => ({
                get value() {
                    return internalValue;
                },
                get isValid() {
                    return checkValidity();
                },
            }),
            [internalValue]
        );

        function handleChange(e: ByzzerChangeEvent<unknown>) {            
            onContextChange?.(e.name as keyof ReportRunConfig, e.value, e.data);
        }

        function checkValidity(): boolean {
            if (requireOmniShopperKPI) {
                return Boolean(internalValue.focusKPI?.length);
            }
        
            return true;
        }        

        return (
            <FilterGroup className={classnames(baseClassName, className)} {...props}>
                <FilterSection onlyRenderIf={includeOmniShopperKPI}>
                    <ByzzerSelect
                        name='focusKPI'
                        value={contextValue.focusKPI}
                        label={
                            <SelectorLabelInfo
                                sku={sku as string}
                                selectorCode={'oshFocusKPI'}
                                required={requireOmniShopperKPI}
                            />
                        }
                        placeholder="Select your KPI"
                        onChange={handleChange}
                        options={preDefinedKPISelectors}
                    />
                </FilterSection>
            </FilterGroup>
        );
    }
);

export default ReportKPIConfigFilters;
